import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificationAPI } from '../../api/services';

interface Notification {
  _id: string;
  type: string;
  title: string;
  message: string;
  link?: string;
  read: boolean;
  createdAt: string;
  metadata?: Record<string, any>;
}

function resolveLink(n: Notification): string | null {
  const m = n.metadata || {};
  const taskId = typeof m.taskId === 'object' ? m.taskId.$oid || m.taskId : m.taskId;
  const projectId = typeof m.projectId === 'object' ? m.projectId.$oid || m.projectId : m.projectId;
  const featureId = typeof m.featureId === 'object' ? m.featureId.$oid || m.featureId : m.featureId;

  switch (n.type) {
    case 'task_assigned':
    case 'task_completed':
      if (taskId) return `/employee/dashboard?taskId=${taskId}`;
      break;
    case 'feature_submitted':
    case 'feature_approved':
    case 'feature_rejected':
      if (featureId) return `/employee/dashboard?featureId=${featureId}`;
      if (projectId) return `/employee/dashboard?projectId=${projectId}`;
      break;
    case 'approval_requested':
    case 'approval_approved':
    case 'approval_rejected':
    case 'approval_escalated':
      return `/admin/dashboard?tab=approvals`;
    case 'evaluation_opened':
    case 'sp_awarded':
      return `/employee/dashboard`;
  }
  if (n.link) return n.link.replace(/[?&]task=/, (match) => match.replace('task=', 'taskId='));
  return null;
}

const TYPE_COLORS: Record<string, string> = {
  task_assigned: 'border-l-blue-500',
  task_completed: 'border-l-green-500',
  evaluation_opened: 'border-l-purple-500',
  approval_requested: 'border-l-yellow-500',
  approval_approved: 'border-l-green-500',
  approval_rejected: 'border-l-red-500',
  approval_escalated: 'border-l-orange-500',
  sp_awarded: 'border-l-yellow-400',
  feature_submitted: 'border-l-indigo-500',
  feature_approved: 'border-l-green-500',
  feature_rejected: 'border-l-red-500',
  general: 'border-l-gray-400',
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

interface Props {
  limit?: number;
  title?: string;
}

const NotificationPanel: React.FC<Props> = ({ limit = 8, title = 'Recent Notifications' }) => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    notificationAPI.getMyNotifications({ limit, unreadOnly: false })
      .then(res => setNotifications(res.data?.data ?? []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [limit]);

  const handleClick = async (n: Notification) => {
    if (!n.read) {
      try {
        await notificationAPI.markAsRead(n._id);
      } catch (err: any) {
        console.error('[NOTIFICATION] markAsRead failed:', n._id, err?.response?.status, err?.response?.data);
      }
    }
    setNotifications(prev => prev.map(x => x._id === n._id ? { ...x, read: true } : x));

    const link = resolveLink(n);
    if (link) {
      navigate(link);
      const url = new URL(link, window.location.origin);
      const taskId = url.searchParams.get('taskId');
      const projectId = url.searchParams.get('projectId');
      if (taskId || projectId) {
        window.dispatchEvent(new CustomEvent('notification-navigate', { detail: { taskId, projectId } }));
      }
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-sm font-semibold text-gray-900 mb-4">{title}</h3>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200">
      <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        {notifications.some(n => !n.read) && (
          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
            {notifications.filter(n => !n.read).length} new
          </span>
        )}
      </div>

      {notifications.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 text-gray-400">
          <svg className="w-8 h-8 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
          </svg>
          <p className="text-xs">No notifications yet</p>
        </div>
      ) : (
        <div className="divide-y divide-gray-50">
          {notifications.map(n => (
            <div
              key={n._id}
              onClick={() => handleClick(n)}
              className={`flex items-start gap-3 px-5 py-3 cursor-pointer border-l-2 transition-colors ${
                TYPE_COLORS[n.type] || TYPE_COLORS.general
              } ${n.read ? 'bg-white hover:bg-gray-50' : 'bg-blue-50/30 hover:bg-blue-50/50'}`}
            >
              <div className="flex-1 min-w-0">
                <p className={`text-sm leading-snug ${n.read ? 'text-gray-600' : 'text-gray-900 font-medium'}`}>
                  {n.message}
                </p>
                <p className="text-xs text-gray-400 mt-0.5">{timeAgo(n.createdAt)}</p>
              </div>
              {!n.read && <div className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0 mt-2" />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default NotificationPanel;
